import React, { useEffect, useRef, useState } from "react";
import "./ProductDetail11.css";
import { Link } from "react-router-dom";
import Gfooter from "../Component/Gfooter";

/* ── Intersection Observer Hook ── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Counter Hook ── */
function useCounter(target, duration = 2200, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const ease = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(ease(p) * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

/* ── Stat Card ── */
function StatCard({ icon, value, suffix, label, delay }) {
  const [ref, inView] = useInView(0.3);
  const count = useCounter(value, 2200, inView);
  return (
    <div className="sc" ref={ref} style={{ animationDelay: `${delay}ms` }}>
      <i className={`bi ${icon} sc__icon`}></i>
      <div className="sc__num">{count}{suffix}</div>
      <div className="sc__label">{label}</div>
    </div>
  );
}

/* ── Benefit Card ── */
function BenCard({ icon, num, suffix, label, desc, delay }) {
  const [ref, inView] = useInView(0.3);
  const count = useCounter(num, 2000, inView);
  return (
    <div className="ben-card" ref={ref} style={{ animationDelay: `${delay}ms` }}>
      <i className={`bi ${icon} ben-card__icon`}></i>
      <div className="ben-card__num">{count}{suffix}</div>
      <div className="ben-card__label">{label}</div>
      <p className="ben-card__desc">{desc}</p>
    </div>
  );
}

/* ── Problem Card ── */
function ProbCard({ icon, title, desc, delay }) {
  return (
    <div className="prob-card" style={{ animationDelay: `${delay}ms` }}>
      <i className={`bi ${icon} prob-card__icon`}></i>
      <h4 className="prob-card__title">{title}</h4>
      <p className="prob-card__desc">{desc}</p>
    </div>
  );
}

/* ── Feature Row ── */
function FeatRow({ icon, tag, title, desc, bullets, img, reverse }) {
  const [ref, inView] = useInView(0.12);
  return (
    <div
      ref={ref}
      className={`feat-row ${reverse ? "feat-row--rev" : ""} ${inView ? "feat-row--visible" : ""}`}
    >
      <div className="feat-row__media">
        <div className="feat-row__img-wrap">
          <img src={img} alt={title} className="feat-row__img" loading="lazy" />
          <div className="feat-row__img-shade" />
        </div>
        <div className="feat-row__tag">{tag}</div>
      </div>
      <div className="feat-row__copy">
        <span className="feat-row__icon-wrap"><i className={`bi ${icon}`}></i></span>
        <h3 className="feat-row__title">{title}</h3>
        <p className="feat-row__desc">{desc}</p>
        {bullets && (
          <ul className="feat-row__list">
            {bullets.map((b, i) => (
              <li key={i}><i className="bi bi-check2-circle"></i> {b}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ── Capability Item ── */
function CapItem({ icon, text }) {
  return (
    <div className="cap-item">
      <i className={`bi ${icon} cap-item__icon`}></i>
      <span>{text}</span>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════ */
function ProductDetail11() {
  const [heroIn, setHeroIn] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [activeUse, setActiveUse] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setHeroIn(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setActiveStep((s) => (s + 1) % 7), 2600);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setActiveUse((s) => (s + 1) % 4), 3200);
    return () => clearInterval(id);
  }, []);

  /* ─── PAGE DATA ─── */
  const problems = [
    {
      icon: "bi-exclamation-triangle",
      title: "Manual Classification Errors",
      desc: "Human operators misclassify vehicles, leading to wrong toll amounts and daily revenue loss across every lane.",
    },
    {
      icon: "bi-shield-x",
      title: "Fraudulent Exemptions",
      desc: "Unauthorized vehicles claim fake exemptions, bypassing payment with zero automated verification in place.",
    },
    {
      icon: "bi-eye-slash",
      title: "No Real-Time Monitoring",
      desc: "Supervisors have zero live visibility into lane activity, creating dangerous operational blind spots.",
    },
    {
      icon: "bi-wifi-off",
      title: "Inaccurate FASTag Transactions",
      desc: "Transaction mismatches go undetected, causing unresolvable disputes and unrecovered revenue leakage.",
    },
    {
      icon: "bi-diagram-3",
      title: "No Centralized Control",
      desc: "Multiple plazas operate in silos — no unified dashboard, no cross-plaza data, no single point of truth.",
    },
    {
      icon: "bi-clock-history",
      title: "Slow Processing & Congestion",
      desc: "Manual checks create queues, frustrate commuters, and drastically reduce hourly throughput capacity.",
    },
  ];

  const features = [
    {
      icon: "bi-camera-video",
      tag: "CORE AI",
      title: "AI-Based Real-Time Monitoring",
      desc: "TPMS continuously monitors all toll lanes using AI-powered cameras and sensors, providing instant alerts for congestion, inactive lanes, or suspicious activities — 24 hours a day, every single day.",
      bullets: [
        "Live camera feed with AI overlay bounding boxes",
        "Instant congestion & anomaly alerts to operators",
        "Automated lane status and boom barrier tracking",
        "Escalation tree for critical incident response",
      ],
      img: "ImagesNew/fLOW-LR.png",
      reverse: false,
    },
    {
      icon: "bi-truck",
      tag: "DETECTION",
      title: "Vehicle & Axle Detection (OCR + AI)",
      desc: "Using deep-learning OCR and AI vision, TPMS automatically identifies vehicle types, license plates, and axle counts with up to 97% accuracy — ensuring correct toll calculation for every vehicle class.",
      bullets: [
        "ANPR / LPR number plate recognition",
        "Axle-count detection for HCVs and multi-axle trucks",
        "97%+ accuracy across all Indian vehicle types",
        "Rear and front plate capture with confidence score",
      ],
      img: "ImagesNew/feature-detection-LR.jpeg",
      reverse: true,
    },
    {
      icon: "bi-credit-card-2-front",
      tag: "FASTAG",
      title: "FASTag Verification System",
      desc: "TPMS validates every FASTag transaction by matching vehicle data with live transaction records, ensuring complete payment transparency and immediately flagging any FASTag–license plate mismatches.",
      bullets: [
        "Real-time RFID FASTag scanning at entry",
        "Vehicle-to-FASTag ID cross-validation",
        "Low balance alerts and top-up notifications",
        "Full IHMCL / NHAI ecosystem compliance",
      ],
      img: "ImagesNew/feature-fraud-LR.jpeg",
      reverse: false,
    },
    {
      icon: "bi-shield-exclamation",
      tag: "SECURITY",
      title: "Fraud Detection & Exemption Control",
      desc: "Unauthorized exemption claims are instantly flagged by verifying vehicles against government and operator databases, preventing misuse and protecting every rupee of toll revenue collected.",
      bullets: [
        "Blacklist & whitelist database real-time matching",
        "Multi-FASTag on single vehicle detection",
        "Speed tolerance and lane violation flagging",
        "Full immutable audit trail per incident",
      ],
      img: "ImagesNew/feature-fastag-LR.jpeg",
      reverse: true,
    },
    {
      icon: "bi-grid-1x2",
      tag: "ANALYTICS",
      title: "Centralized Dashboard & Analytics",
      desc: "Monitor multiple toll plazas from a single command dashboard with real-time insights on traffic volumes, revenue streams, lane performance timings, and AI camera feeds — all updating every second.",
      bullets: [
        "Multi-plaza unified command overview",
        "Revenue today vs. yesterday comparison",
        "Lane-level average wait time charts",
        "One-click PDF / CSV / XLS report export",
      ],
      img: "ImagesNew/dashboard-LR-1.jpeg",
      reverse: false,
    },
    {
      icon: "bi-file-earmark-bar-graph",
      tag: "REPORTING",
      title: "Audit-Ready Reports",
      desc: "Generate detailed compliance reports including vehicle images, precise 4K timestamps, transaction logs, and performance summaries — ensuring regulatory compliance and making audits completely effortless.",
      bullets: [
        "Timestamped vehicle image logs per transaction",
        "Bulk Excel data upload and export support",
        "Scheduled delivery to management stakeholders",
        "Role-based access control for all reports",
      ],
      img: "ImagesNew/reports-LR.jpeg",
      reverse: true,
    },
  ];

  const steps = [
    { icon: "bi-sign-intersection-side", title: "Vehicle Enters Lane",    desc: "Loop sensors & AI cameras activate as vehicle approaches the plaza." },
    { icon: "bi-camera2",                title: "AI Camera Captures",     desc: "High-speed camera captures full vehicle image with frame precision." },
    { icon: "bi-upc-scan",               title: "OCR + LPR Detection",    desc: "OCR reads license plate; AI classifies vehicle type and axle count." },
    { icon: "bi-credit-card",            title: "FASTag Verification",    desc: "RFID scans FASTag; system cross-validates against the live cloud DB." },
    { icon: "bi-check-circle",           title: "Exemption Check",        desc: "Vehicle is matched against exemption & blacklist databases instantly." },
    { icon: "bi-cloud-upload",           title: "Cloud Data Storage",     desc: "All data timestamped and stored in secure, encrypted cloud database." },
    { icon: "bi-speedometer2",           title: "Dashboard Update",       desc: "Live dashboard refreshes; boom barrier cleared; reports updated." },
  ];

  const benefits = [
    { icon: "bi-currency-rupee",   num: 25,  suffix: "%",  label: "Revenue Leakage Reduced",     desc: "AI verification eliminates manual bypass and FASTag fraud at source." },
    { icon: "bi-lightning-charge", num: 21,  suffix: "s",  label: "Avg. Lane Clearance Time",    desc: "From vehicle arrival to barrier lift — 21 seconds, every time." },
    { icon: "bi-eye",              num: 100, suffix: "%",  label: "Operational Transparency",    desc: "Every lane, every transaction, every second — fully visible." },
    { icon: "bi-graph-up-arrow",   num: 97,  suffix: "%",  label: "Detection Accuracy",          desc: "AI OCR and axle detection tested across all Indian vehicle classes." },
    { icon: "bi-shield-check",     num: 0,   suffix: "%",  label: "Undetected Fraud Attempts",   desc: "Multi-layer rules catch every suspicious transaction in real time." },
    { icon: "bi-buildings",        num: 24,  suffix: "/7", label: "Live Multi-Plaza Monitoring", desc: "Centralized cloud dashboard active for all plaza locations at once." },
  ];

  const capabilities = [
    { icon: "bi-camera-video-fill",   text: "24×7 AI Camera Monitoring" },
    { icon: "bi-lightning",           text: "Real-Time Data Collection" },
    { icon: "bi-diagram-3-fill",      text: "Multi-Location Integration" },
    { icon: "bi-display",             text: "Camera Status Monitoring" },
    { icon: "bi-cast",                text: "Live Streaming of Toll Operations" },
    { icon: "bi-robot",               text: "AI-Based Automated Reporting" },
    { icon: "bi-file-spreadsheet",    text: "Bulk Data Upload (Excel Support)" },
    { icon: "bi-camera-reels",        text: "Vehicle Image & Video Recording" },
    { icon: "bi-bar-chart-line-fill", text: "Toll Naka Performance Grading" },
    { icon: "bi-cloud-check-fill",    text: "Cloud-Based Multi-Plaza System" },
    { icon: "bi-person-badge",        text: "Role-Based Secure Access" },
    { icon: "bi-shield-lock-fill",    text: "End-to-End Encrypted Transactions" },
  ];

  const useCases = [
    {
      icon: "bi-sign-merge-right-fill",
      title: "National Highways",
      desc: "Full ANPR + FASTag automation across NHAI-managed highway corridors with centralized audit compliance.",
      img: "ImagesNew/feature-detection-LR.jpeg",
    },
    {
      icon: "bi-building",
      title: "Smart Cities",
      desc: "Integrated urban toll monitoring feeding real-time traffic data directly into smart city management systems.",
      img: "ImagesNew/Hero-Image-2-LR.png",
    },
    {
      icon: "bi-briefcase-fill",
      title: "Private Toll Operators",
      desc: "Revenue assurance and fraud prevention built for privately operated toll concessions — BOT and HAM models.",
      img: "ImagesNew/feature-monitoring3.jpeg",
    },
    {
      icon: "bi-bank2",
      title: "Government Monitoring",
      desc: "Independent AI audit layer for government bodies to verify toll collection performance and compliance reports.",
      img: "ImagesNew/dashboard-LR-1.jpeg",
    },
  ];

  return (
    <div className="tp-page">

      {/* ════ 1. HERO ════ */}
      <section className="tp-hero">
        <video className="tp-hero__video" autoPlay loop muted playsInline>
          <source src="video/TPMS_video.mp4" type="video/mp4" />
        </video>
        <div className="tp-hero__scrim" />

        <div className={`tp-hero__layout ${heroIn ? "tp-hero__layout--in" : ""}`}>

          {/* LEFT */}
          <div className="tp-hero__left">
            <div className="tp-hero__badge">
              <span className="tp-hero__pulse" />
              AI-Powered &nbsp;·&nbsp; NHAI Compliant &nbsp;·&nbsp; Real-Time
            </div>
            <h1 className="tp-hero__h1">
              Transforming Toll<br />
              Operations with<br />
              <em>AI-Powered TPMS</em>
            </h1>
            <p className="tp-hero__tagline">
              Real-Time Monitoring &nbsp;|&nbsp; 100% Transparency &nbsp;|&nbsp;
              Fraud Prevention &nbsp;|&nbsp; Smart Analytics
            </p>
            <p className="tp-hero__body">
              TPMS is an advanced AI-driven solution designed to revolutionize toll plaza
              operations — from real-time vehicle tracking to intelligent analytics and
              fraud detection, ensuring accuracy and complete operational control across
              every lane.
            </p>
            <div className="tp-hero__cta">
              <Link to="/contact-us">
                <button className="tp-btn tp-btn--primary">
                  <i className="bi bi-play-circle-fill"></i> Get Live Demo
                </button>
              </Link>
              <Link to="/contact-us">
                <button className="tp-btn tp-btn--ghost">
                  <i className="bi bi-headset"></i> Talk to Expert
                </button>
              </Link>
            </div>
            <div className="tp-hero__bc">
              <span>Home</span>
              <i className="bi bi-chevron-right"></i>
              <span>Products</span>
              <i className="bi bi-chevron-right"></i>
              <b>TPMS</b>
            </div>
          </div>

          {/* RIGHT HUD */}
          <div className="tp-hero__right">
            <div className="tp-hud">
              <div className="tp-hud__status">
                <span className="tp-hud__dot tp-hud__dot--green"></span>
                SYSTEM STATUS: <strong>OPERATIONAL — ALL LANES ACTIVE</strong>
              </div>
              <div className="tp-hud__divider" />
              <div className="tp-hud__grid">
                <div className="tp-hud__cell">
                  <i className="bi bi-bullseye"></i>
                  <span>AI ACCURACY</span>
                  <strong>98.9%</strong>
                </div>
                <div className="tp-hud__cell">
                  <i className="bi bi-layout-three-columns"></i>
                  <span>LANES ACTIVE</span>
                  <strong>ALL 8</strong>
                </div>
                <div className="tp-hud__cell">
                  <i className="bi bi-stopwatch"></i>
                  <span>CLEARANCE</span>
                  <strong>21s</strong>
                </div>
                <div className="tp-hud__cell">
                  <i className="bi bi-car-front-fill"></i>
                  <span>VEH / HOUR</span>
                  <strong>120+</strong>
                </div>
              </div>
              <div className="tp-hud__divider" />
              <div className="tp-hud__bars">
                <div className="tp-hud__bar-row">
                  <span>FASTag Utilisation</span>
                  <div className="tp-hud__track">
                    <div className="tp-hud__fill" style={{ width: "94%" }} />
                  </div>
                  <strong>94%</strong>
                </div>
                <div className="tp-hud__bar-row">
                  <span>Revenue vs Target</span>
                  <div className="tp-hud__track">
                    <div className="tp-hud__fill" style={{ width: "87%" }} />
                  </div>
                  <strong>87%</strong>
                </div>
                <div className="tp-hud__bar-row">
                  <span>AI Accuracy</span>
                  <div className="tp-hud__track">
                    <div className="tp-hud__fill" style={{ width: "99%" }} />
                  </div>
                  <strong>99%</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <a href="#tp-problem" className="tp-hero__scroll-cue">
          <i className="bi bi-chevron-double-down"></i>
        </a>
      </section>

      {/* ════ 2. STATS BAR ════ */}
      <div className="tp-stats">
        <div className="tp-wrap tp-stats__row">
          <StatCard icon="bi-bullseye"       value={97}  suffix="%" label="Vehicle Detection Accuracy" delay={0}   />
          <div className="tp-stats__sep" />
          <StatCard icon="bi-clock"          value={21}  suffix="s" label="Avg. Lane Clearance"        delay={100} />
          <div className="tp-stats__sep" />
          <StatCard icon="bi-currency-rupee" value={25}  suffix="%" label="Revenue Leakage Reduced"    delay={200} />
          <div className="tp-stats__sep" />
          <StatCard icon="bi-car-front-fill" value={120} suffix="+" label="Vehicles / Hour / Lane"     delay={300} />
          <div className="tp-stats__sep" />
          <StatCard icon="bi-shield-check"   value={100} suffix="%" label="Operational Transparency"   delay={400} />
        </div>
      </div>

      {/* ════ 3. PROBLEM ════ */}
      <section className="tp-section" id="tp-problem">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">THE CHALLENGE</p>
            <h2 className="tp-h2">Challenges in<br /><span>Traditional Toll Systems</span></h2>
            <p className="tp-sub">
              Traditional toll systems face multiple operational challenges that lead to revenue
              loss and inefficiencies — leaving operators vulnerable and highways congested.
            </p>
          </div>
          <div className="prob-grid">
            {problems.map((p, i) => (
              <ProbCard key={i} {...p} delay={i * 80} />
            ))}
          </div>
          <div className="prob-result">
            <i className="bi bi-exclamation-octagon-fill"></i>
            Result? Revenue leakage, slow operations, and zero transparency across your network.
          </div>
        </div>
      </section>

      {/* ════ 4. SOLUTION ════ */}
      <section className="tp-section tp-solution">
        <div className="tp-wrap tp-solution__inner">
          <div className="tp-solution__media">
            <img src="ImagesNew/Hero-Image-2-LR.png" alt="TPMS system overview" loading="lazy" />
            <div className="tp-solution__frame" />
            <div className="tp-solution__chip">
              <i className="bi bi-patch-check-fill"></i> NHAI Verified System
            </div>
          </div>
          <div className="tp-solution__copy">
            <p className="tp-eyebrow">THE SOLUTION</p>
            <h2 className="tp-h2">Introducing TPMS –<br /><span>Smart Toll Monitoring</span></h2>
            <p className="tp-body">
              TPMS is a next-generation AI-powered toll monitoring system that automates,
              monitors, and optimizes toll operations in real time. With intelligent surveillance,
              OCR-based vehicle detection, and centralized analytics, TPMS ensures every
              transaction is accurate and every lane is fully visible.
            </p>
            <div className="tp-pills">
              {["Accurate Toll Collection", "Real-Time Monitoring", "Fraud Detection", "Data-Driven Decisions"].map((t, i) => (
                <span key={i} className="tp-pill"><i className="bi bi-check-lg"></i> {t}</span>
              ))}
            </div>
            <Link to="/contact-us">
              <button className="tp-btn tp-btn--primary">
                Explore TPMS <i className="bi bi-arrow-right"></i>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ════ 5. FEATURES ════ */}
      <section className="tp-section tp-features" id="tp-features">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">CAPABILITIES</p>
            <h2 className="tp-h2">Core Features That<br /><span>Set TPMS Apart</span></h2>
          </div>
          <div className="feat-rows">
            {features.map((f, i) => (
              <FeatRow key={i} {...f} reverse={i % 2 !== 0} />
            ))}
          </div>
        </div>
      </section>

      {/* ════ 6. HOW IT WORKS ════ */}
      <section className="tp-section tp-flow" id="tp-flow">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">HOW IT WORKS</p>
            <h2 className="tp-h2">From Vehicle Approach<br /><span>to Full Clearance</span></h2>
            <p className="tp-sub">7 intelligent steps — completed in under 21 seconds.</p>
          </div>
          <div className="tp-flow__diagram">
            <img src="ImagesNew/Hero-Image-LR.png" alt="TPMS process flow diagram" loading="lazy" />
            <div className="tp-flow__fade" />
          </div>
          <div className="tp-flow__steps">
            {steps.map((s, i) => (
              <div key={i} className={`tp-flow__step ${activeStep === i ? "tp-flow__step--on" : ""}`}>
                <div className="tp-flow__num">0{i + 1}</div>
                <i className={`bi ${s.icon} tp-flow__ico`}></i>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="tp-flow__arrow">
                    <i className="bi bi-arrow-right-short"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="tp-flow__track">
            {steps.map((_, i) => (
              <div key={i} className={`tp-flow__dot ${activeStep === i ? "tp-flow__dot--on" : ""}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ════ 7. DASHBOARD ════ */}
      <section className="tp-section tp-dash">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">LIVE DASHBOARD</p>
            <h2 className="tp-h2">Every Lane. Every Vehicle.<br /><span>One Screen.</span></h2>
            <p className="tp-sub">
              The TPMS command dashboard aggregates vehicle counts, revenue stats, FASTag
              utilization, lane performance timings, and AI camera feeds — updated every second.
            </p>
          </div>
          <div className="tp-dash__hero">
            <img src="ImagesNew/dashboard-LR-1.jpeg" alt="TPMS main dashboard" loading="lazy" />
            <div className="tp-dash__frame" />
          </div>
          <div className="tp-dash__row">
            {[
              { img: "ImagesNew/feature-detection-LR-1.jpeg", label: "AI Detection Feed",   icon: "bi-camera-video" },
              { img: "ImagesNew/feature-fastag-LR.jpeg",      label: "FASTag Verification", icon: "bi-credit-card-2-front" },
              { img: "ImagesNew/reports-Image-LR.jpeg",        label: "Analytics & Reports", icon: "bi-bar-chart-line" },
            ].map((t, i) => (
              <div key={i} className="tp-dash__thumb">
                <img src={t.img} alt={t.label} loading="lazy" />
                <div className="tp-dash__thumb-label">
                  <i className={`bi ${t.icon}`}></i> {t.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ 8. BENEFITS ════ */}
      <section className="tp-section tp-benefits">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">BUSINESS IMPACT</p>
            <h2 className="tp-h2">Measurable Benefits<br /><span>for Every Operator</span></h2>
          </div>
          <div className="ben-grid">
            {benefits.map((b, i) => (
              <BenCard key={i} {...b} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* ════ 9. CAPABILITIES ════ */}
      <section className="tp-section tp-cap">
        <div className="tp-wrap tp-cap__inner">
          <div className="tp-cap__left">
            <p className="tp-eyebrow">SYSTEM CAPABILITIES</p>
            <h2 className="tp-h2">Built for 24×7<br /><span>Heavy-Duty Operations</span></h2>
            <p className="tp-body">
              TPMS is engineered for the demands of high-volume Indian highway
              infrastructure — scalable from a 2-lane booth to a 50-lane expressway
              interchange without any architectural changes.
            </p>
            <p className="tp-body">
              Our AI models retrain continuously on new vehicle types, lighting conditions,
              and regional plates, ensuring performance improvements are automatic.
            </p>
            <Link to="/contact-us">
              <button className="tp-btn tp-btn--primary">
                Request Technical Brief <i className="bi bi-arrow-right"></i>
              </button>
            </Link>
          </div>
          <div className="tp-cap__right">
            <div className="cap-grid">
              {capabilities.map((c, i) => (
                <CapItem key={i} {...c} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ════ 10. ARCHITECTURE ════ */}
      <section className="tp-section tp-arch">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">SYSTEM ARCHITECTURE</p>
            <h2 className="tp-h2">Cloud-Native &amp; Scalable<br /><span>End-to-End Architecture</span></h2>
            <p className="tp-sub">
              From edge cameras at the plaza to secure cloud storage and the operator
              dashboard — every component is connected, redundant, and failsafe.
            </p>
          </div>
          <div className="tp-arch__wrap">
            <img src="ImagesNew/fLOW-LR.png" alt="TPMS system architecture" loading="lazy" />
            <div className="tp-arch__overlay" />
          </div>
        </div>
      </section>

      {/* ════ 11. USE CASES ════ */}
      <section className="tp-section tp-use">
        <div className="tp-wrap">
          <div className="tp-head tp-head--center">
            <p className="tp-eyebrow">USE CASES</p>
            <h2 className="tp-h2">Who Uses TPMS?<br /><span>Industries We Serve</span></h2>
          </div>
          <div className="tp-use__grid">
            {useCases.map((u, i) => (
              <div
                key={i}
                className={`tp-use__card ${activeUse === i ? "tp-use__card--on" : ""}`}
                onMouseEnter={() => setActiveUse(i)}
              >
                <div className="tp-use__img-wrap">
                  <img src={u.img} alt={u.title} loading="lazy" />
                  <div className="tp-use__img-fade" />
                </div>
                <div className="tp-use__body">
                  <i className={`bi ${u.icon} tp-use__ico`}></i>
                  <h4>{u.title}</h4>
                  <p>{u.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ 12. REPORTS ════ */}
      <section className="tp-section tp-reports">
        <div className="tp-wrap tp-reports__inner">
          <div className="tp-reports__copy">
            <p className="tp-eyebrow">REPORTING</p>
            <h2 className="tp-h2">Audit-Ready Reports<br /><span>at Every Level</span></h2>
            <p className="tp-body">
              Generate detailed compliance reports including vehicle images, precise timestamps,
              transaction logs, and performance summaries — ensuring full regulatory compliance
              and making audits completely effortless for your team.
            </p>
            <ul className="tp-reports__list">
              <li><i className="bi bi-check2-circle"></i> Timestamped vehicle image logs per transaction</li>
              <li><i className="bi bi-check2-circle"></i> Hourly, daily and monthly revenue summaries</li>
              <li><i className="bi bi-check2-circle"></i> PDF / CSV / XLS one-click export</li>
              <li><i className="bi bi-check2-circle"></i> Scheduled delivery to management stakeholders</li>
              <li><i className="bi bi-check2-circle"></i> Role-based access control for report viewing</li>
            </ul>
            <Link to="/contact-us">
              <button className="tp-btn tp-btn--primary">
                <i className="bi bi-file-earmark-arrow-down"></i> See Sample Report
              </button>
            </Link>
          </div>
          <div className="tp-reports__media">
            <img src="ImagesNew/reports-LR.jpeg" alt="TPMS reports" loading="lazy" />
            <div className="tp-reports__frame" />
          </div>
        </div>
      </section>

      {/* ════ 13. CTA ════ */}
      <section className="tp-cta">
        <div className="tp-cta__bg" />
        <div className="tp-cta__inner">
          <i className="bi bi-rocket-takeoff-fill tp-cta__rocket"></i>
          <p className="tp-eyebrow">GET STARTED TODAY</p>
          <h2 className="tp-cta__h2">
            Ready to Upgrade Your<br /><span>Toll Infrastructure?</span>
          </h2>
          <p className="tp-cta__body">
            Experience the future of toll monitoring with TPMS. Improve efficiency,
            eliminate fraud, and gain complete control over your toll operations.
            Book a free demo today.
          </p>
          <div className="tp-cta__chips">
            <span><i className="bi bi-check-circle-fill"></i> Free Demo Available</span>
            <span><i className="bi bi-check-circle-fill"></i> NHAI Compliant</span>
            <span><i className="bi bi-check-circle-fill"></i> 24/7 Support</span>
          </div>
          <div className="tp-cta__btns">
            <Link to="/contact-us">
              <button className="tp-btn tp-btn--primary tp-btn--lg">
                <i className="bi bi-calendar2-check-fill"></i> Book Free Demo
              </button>
            </Link>
            <Link to="/contact-us">
              <button className="tp-btn tp-btn--ghost tp-btn--lg">
                <i className="bi bi-telephone-fill"></i> Talk to Expert
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Gfooter />
    </div>
  );
}

export default ProductDetail11;